// ════════════════════════════════════════════════════════════
// toast — Wrapper ECR sur l'API impérative Sonner
// ════════════════════════════════════════════════════════════
// Réexpose `toast()` + 5 variants natifs Sonner (success/error/
// warning/info/loading) avec :
//   - reportToSentry opt-in (log Sentry.captureException + contexte)
//   - cause (erreur originale passée à Sentry)
//   - Composition avec les shortcuts métier (cf. toast-shortcuts.ts)
//
// Usage :
//   import { toast } from '@/components/ui/toast';
//   toast.success("Voyage MSY-042-05-26 livré");
//   toast.error("Erreur réseau", { reportToSentry: true, cause: err });
//   toast.error.network();
//   toast.savedAs("MSY-042-05-26");
//
// ⚠️ Ne pas wrapper Sonner avec Framer Motion : Sonner gère ses
// propres animations spring. `prefers-reduced-motion` géré par Sonner.
// ════════════════════════════════════════════════════════════
import { toast as sonnerToast, type ExternalToast } from 'sonner';
import * as Sentry from '@sentry/react';
import type { ToastOptions, ToastVariant } from './types';
import { attachShortcuts, type ToastWithShortcuts } from './toast-shortcuts';

type ToastId = string | number;
type ToastMessage = Parameters<typeof sonnerToast>[0];

// ─── Sentry helper ──────────────────────────────────────────

function reportIfNeeded(
  variant: ToastVariant,
  message: ToastMessage,
  options: ToastOptions | undefined,
): void {
  if (!options?.reportToSentry) return;
  const error =
    options.cause instanceof Error
      ? options.cause
      : new Error(typeof message === 'string' ? message : `Toast ${variant}`);
  Sentry.captureException(error, {
    extra: {
      toastVariant: variant,
      toastMessage: typeof message === 'string' ? message : '[ReactNode]',
      toastDescription: typeof options.description === 'string' ? options.description : undefined,
    },
  });
}

/** Sépare les options ECR (reportToSentry/cause) des options Sonner. */
function splitOptions(options?: ToastOptions): {
  sonner: ExternalToast | undefined;
  report: boolean;
} {
  if (!options) return { sonner: undefined, report: false };
  // On extrait nos clés sans les passer à Sonner (évite tout warning).
  const { reportToSentry, cause: _cause, ...sonner } = options;
  return { sonner, report: Boolean(reportToSentry) };
}

// ─── Variants ────────────────────────────────────────────────

function callVariant(
  variant: ToastVariant,
  message: ToastMessage,
  options?: ToastOptions,
): ToastId {
  reportIfNeeded(variant, message, options);
  const { sonner } = splitOptions(options);
  switch (variant) {
    case 'success':
      return sonnerToast.success(message, sonner);
    case 'error':
      return sonnerToast.error(message, sonner);
    case 'warning':
      return sonnerToast.warning(message, sonner);
    case 'info':
      return sonnerToast.info(message, sonner);
    case 'loading':
      return sonnerToast.loading(message, sonner);
    case 'default':
    default:
      return sonnerToast(message, sonner);
  }
}

// ─── Fonction de base callable ──────────────────────────────

function baseToast(message: ToastMessage, options?: ToastOptions): ToastId {
  return callVariant('default', message, options);
}

// ─── toast.error avec sous-shortcuts ─────────────────────────
// `toast.error` doit être appelable ET exposer .network/.permission/.generic.
// On crée la fonction puis on lui colle les méthodes via Object.assign.

function errorFn(message: ToastMessage, options?: ToastOptions): ToastId {
  return callVariant('error', message, options);
}

const errorWithShortcuts = Object.assign(errorFn, {
  /** Erreur réseau — Sentry opt-in activé par défaut. */
  network(options?: ToastOptions): ToastId {
    return errorFn('Erreur réseau', {
      description: 'Vérifiez votre connexion et réessayez.',
      reportToSentry: true,
      ...options,
    });
  },
  /** Action refusée par le contrôle d'accès (rôle/RLS). Pas de Sentry. */
  permission(options?: ToastOptions): ToastId {
    return errorFn('Action non autorisée', {
      description: "Vous n'avez pas les droits pour cette action.",
      ...options,
    });
  },
  /** Fallback générique — Sentry opt-in activé par défaut. */
  generic(err?: Error, options?: ToastOptions): ToastId {
    return errorFn('Une erreur est survenue', {
      description: err?.message,
      reportToSentry: true,
      cause: err,
      ...options,
    });
  },
});

// ─── API publique ────────────────────────────────────────────

const toastBase = Object.assign(baseToast, {
  success: (message: ToastMessage, options?: ToastOptions) =>
    callVariant('success', message, options),
  error: errorWithShortcuts,
  warning: (message: ToastMessage, options?: ToastOptions) =>
    callVariant('warning', message, options),
  info: (message: ToastMessage, options?: ToastOptions) => callVariant('info', message, options),
  loading: (message: ToastMessage, options?: ToastOptions) =>
    callVariant('loading', message, options),
  /** Réexposition directe Sonner (gère loading → success/error automatique). */
  promise: sonnerToast.promise.bind(sonnerToast),
  /** Ferme un toast par id, ou tous si appelé sans argument. */
  dismiss: (id?: ToastId): ToastId => sonnerToast.dismiss(id),
});

// Attache les shortcuts métier (saved/savedAs/deleted) au même objet.
const toast: ToastWithShortcuts<typeof toastBase> = attachShortcuts(toastBase);

export { toast };
